import { useEffect, useState } from "react";
import { useFirestore } from "react-redux-firebase";
import { FIRESTORE_PURCHASE_ORDER_INVOICES_TABLE } from "../../constants/FirestoreConstant";

/**
 * Hook to fetch and manage purchase order invoices from Firestore
 * @param {Object} options - Configuration options
 * @param {boolean} options.realtime - Whether to use real-time listener (default: true)
 * @param {string} options.status - Filter by status (optional)
 * @param {string} options.supplierId - Filter by supplier ID (optional)
 * @param {string} options.shipmentId - Filter by shipment ID (optional)
 * @returns {Object} - Invoices data and state
 */
export const usePurchaseOrderInvoices = (options = {}) => {
  const { realtime = true, status = null, supplierId = null, shipmentId = null } = options;
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const firestore = useFirestore();

  useEffect(() => {
    setLoading(true);
    let query = firestore.collection(FIRESTORE_PURCHASE_ORDER_INVOICES_TABLE);

    // Apply filters
    if (status) {
      query = query.where("status", "==", status);
    }
    if (supplierId) {
      query = query.where("supplierId", "==", supplierId);
    }
    if (shipmentId) {
      query = query.where("shipmentId", "==", shipmentId);
    }

    // Order by creation date
    query = query.orderBy("createdAt", "desc");

    if (realtime) {
      const unsubscribe = query.onSnapshot(
        (snapshot) => {
          const data = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          setInvoices(data);
          setLoading(false);
          setError(null);
        },
        (err) => {
          console.error("Error fetching purchase order invoices:", err);
          setError(err);
          setLoading(false);
        },
      );

      return () => unsubscribe();
    } else {
      query
        .get()
        .then((snapshot) => {
          const data = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          setInvoices(data);
          setLoading(false);
        })
        .catch((err) => {
          console.error("Error fetching purchase order invoices:", err);
          setError(err);
          setLoading(false);
        });
    }
  }, [firestore, realtime, status, supplierId, shipmentId]);

  return { invoices, loading, error };
};

/**
 * Hook to fetch a single purchase order invoice with a real-time listener for
 * its history subcollection.
 * @param {string} id - The invoice document ID
 * @returns {Object} - { invoice, loading, invoiceHistory, historyLoading, error }
 */
export const usePurchaseOrderInvoice = (id) => {
  const firestore = useFirestore();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [invoiceHistory, setInvoiceHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    const unsubscribe = firestore
      .collection(FIRESTORE_PURCHASE_ORDER_INVOICES_TABLE)
      .doc(id)
      .onSnapshot(
        (doc) => {
          setInvoice(doc.exists ? { id: doc.id, ...doc.data() } : null);
          setLoading(false);
        },
        (err) => {
          console.error("Error listening to invoice:", err);
          setError(err);
          setLoading(false);
        },
      );
    return () => unsubscribe();
  }, [firestore, id]);

  useEffect(() => {
    if (!id) return;
    const unsubscribe = firestore
      .collection(FIRESTORE_PURCHASE_ORDER_INVOICES_TABLE)
      .doc(id)
      .collection("history")
      .onSnapshot(
        (snapshot) => {
          const sorted = snapshot.docs
            .map((doc) => ({ id: doc.id, ...doc.data() }))
            .sort((a, b) => (b.changedAt || 0) - (a.changedAt || 0));
          setInvoiceHistory(sorted);
          setHistoryLoading(false);
        },
        (err) => {
          console.error("Error loading invoice history:", err);
          setHistoryLoading(false);
        },
      );
    return () => unsubscribe();
  }, [firestore, id]);

  return { invoice, loading, invoiceHistory, historyLoading, error };
};
